import { ArrowLeft, Download, Upload, Wand2 } from "lucide-react";
import { useMemo, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Button, cx, ErrorNote, Modal, Spinner, toast } from "../components/ui";
import { api, useApi } from "../lib/api";
import { useConnections } from "../lib/connections";

interface Mapping {
  actor: string;
  actorName?: string;
  actorEmail?: string;
  source: "manual" | "auto" | "csv";
  verifiedAt: string;
  by: string;
}
interface Row {
  email: string;
  name?: string;
  status: "invited" | "active" | "disabled";
  mapping: Mapping | null;
}
interface AutoMatch {
  applied: boolean;
  matched: { email: string; actor: string; actorName?: string }[];
  ambiguous: { email: string; actors: string[] }[];
  unmatched: string[];
  truncated: boolean;
  actorsRead: number;
}
interface ImportResult {
  applied: boolean;
  results: { line: number; email: string; actor: string; ok: boolean; error?: string }[];
  truncated: boolean;
}

const SOURCE = { manual: "Set by hand", auto: "Auto-matched", csv: "Imported" };

function download(name: string, text: string) {
  const url = URL.createObjectURL(new Blob([text], { type: "text/csv" }));
  const a = Object.assign(document.createElement("a"), { href: url, download: name });
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
const csvCell = (v: string | undefined) => (/[",\n]/.test(v ?? "") ? `"${(v ?? "").replace(/"/g, '""')}"` : (v ?? ""));

/** Tenant admin: portal user → Infor actor for one connection (design §6.3). */
export default function ActorMapping() {
  const { connId = "" } = useParams();
  const { data, error, loading, reload: reloadRows } = useApi<{ rows: Row[] }>(`/admin/connections/${connId}/actors`);
  const { reload: reloadConnections } = useConnections();
  // A saved mapping can change which connections the signed-in user may use: refresh both.
  const reload = async () => {
    await reloadRows();
    await reloadConnections();
  };
  const [unmappedOnly, setUnmappedOnly] = useState(false);
  const [auto, setAuto] = useState<AutoMatch | null>(null);
  const [imported, setImported] = useState<{ csv: string; result: ImportResult } | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [problem, setProblem] = useState<string | null>(null);
  const file = useRef<HTMLInputElement>(null);

  const rows = useMemo(() => (data?.rows ?? []).filter((r) => !unmappedOnly || (!r.mapping && r.status !== "disabled")), [data, unmappedOnly]);
  const unmapped = (data?.rows ?? []).filter((r) => !r.mapping && r.status !== "disabled").length;

  const run = async (label: string, fn: () => Promise<void>) => {
    setBusy(label);
    setProblem(null);
    try {
      await fn();
    } catch (e) {
      setProblem((e as Error).message);
    } finally {
      setBusy(null);
    }
  };
  const previewAuto = () => run("auto", async () => setAuto(await api<AutoMatch>(`/admin/connections/${connId}/actors/automatch`, { body: { apply: false } })));
  const applyAuto = () =>
    run("auto-apply", async () => {
      const r = await api<AutoMatch>(`/admin/connections/${connId}/actors/automatch`, { body: { apply: true } });
      setAuto(null);
      toast(`${r.matched.length} user${r.matched.length === 1 ? "" : "s"} mapped`);
      await reload();
    });
  const previewImport = (f: File | undefined) =>
    f &&
    run("import", async () => {
      const csv = await f.text();
      setImported({ csv, result: await api<ImportResult>(`/admin/connections/${connId}/actors/import`, { body: { csv, apply: false } }) });
      if (file.current) file.current.value = "";
    });
  const applyImport = () =>
    imported &&
    run("import-apply", async () => {
      const r = await api<ImportResult>(`/admin/connections/${connId}/actors/import`, { body: { csv: imported.csv, apply: true } });
      setImported(null);
      toast(`${r.results.filter((x) => x.ok).length} mappings imported`);
      await reload();
    });
  const exportCsv = () =>
    download(
      `actors-${connId}.csv`,
      ["email,actor,actor_name,source,status", ...(data?.rows ?? []).map((r) => [r.email, r.mapping?.actor, r.mapping?.actorName, r.mapping ? r.mapping.source : "unmapped", r.status].map(csvCell).join(","))].join("\n") + "\n",
    );

  return (
    <div>
      <Link to="/admin/connections" className="inline-flex items-center gap-1 text-sm text-accent-text hover:underline mb-3">
        <ArrowLeft className="size-4" aria-hidden /> Connections
      </Link>
      <div className="flex flex-wrap items-end justify-between gap-3 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-ink">Actor mapping · {connId}</h1>
          <p className="text-sm text-muted">Link each portal user to their Infor actor on this connection. A user with no actor can't use this connection.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button busy={busy === "auto"} onClick={() => void previewAuto()}>
            <Wand2 className="size-4" aria-hidden /> Auto-match by email
          </Button>
          <input ref={file} type="file" accept=".csv,text/csv" className="hidden" onChange={(e) => void previewImport(e.target.files?.[0])} />
          <Button busy={busy === "import"} onClick={() => file.current?.click()}>
            <Upload className="size-4" aria-hidden /> Import CSV
          </Button>
          <Button onClick={exportCsv} disabled={!data}>
            <Download className="size-4" aria-hidden /> Export CSV
          </Button>
        </div>
      </div>
      {(error || problem) && (
        <div className="mb-4">
          <ErrorNote>{error ?? problem}</ErrorNote>
        </div>
      )}
      {loading && !data ? (
        <Spinner />
      ) : (
        <>
          <div className="flex items-center justify-between mb-2 text-sm">
            <span className={cx(unmapped ? "text-warn" : "text-good")}>{unmapped ? `${unmapped} user${unmapped === 1 ? "" : "s"} not mapped` : "Everyone is mapped"}</span>
            <label className="flex items-center gap-2 text-ink-2">
              <input type="checkbox" checked={unmappedOnly} onChange={(e) => setUnmappedOnly(e.target.checked)} /> Unmapped only
            </label>
          </div>
          <div className="overflow-x-auto rounded-xl border border-line bg-surface">
            <table className="w-full text-sm">
              <thead className="text-left text-muted border-b border-line">
                <tr>
                  <th className="px-4 py-2 font-medium">User</th>
                  <th className="px-4 py-2 font-medium">Infor actor</th>
                  <th className="px-4 py-2 font-medium">Checked</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <MappingRowView key={r.email} connId={connId} row={r} onSaved={() => void reload()} />
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {auto && (
        <Modal
          title="Auto-match by email"
          onClose={() => setAuto(null)}
          wide
          footer={
            <>
              <Button onClick={() => setAuto(null)}>Cancel</Button>
              <Button variant="primary" busy={busy === "auto-apply"} disabled={!auto.matched.length} onClick={() => void applyAuto()}>
                Map {auto.matched.length} user{auto.matched.length === 1 ? "" : "s"}
              </Button>
            </>
          }
        >
          <div className="space-y-4 text-sm">
            <p className="text-muted">
              Compared {auto.actorsRead} active Infor actors with unmapped portal users, ignoring case. Nothing is saved until you confirm.
              {auto.truncated && " Infor had more actors than the portal reads at once (10,000), so some users may be missed; use CSV import for them."}
            </p>
            <Section title={`Will be mapped (${auto.matched.length})`} items={auto.matched.map((m) => `${m.email} → ${m.actor}${m.actorName ? ` (${m.actorName})` : ""}`)} />
            <Section title={`More than one actor has this email (${auto.ambiguous.length})`} tone="warn" items={auto.ambiguous.map((a) => `${a.email}: ${a.actors.join(", ")}`)} />
            <Section title={`No actor with this email (${auto.unmatched.length})`} tone="muted" items={auto.unmatched} />
          </div>
        </Modal>
      )}

      {imported && (
        <Modal
          title="Import actor mapping"
          onClose={() => setImported(null)}
          wide
          footer={
            <>
              <Button onClick={() => setImported(null)}>Cancel</Button>
              <Button variant="primary" busy={busy === "import-apply"} disabled={!imported.result.results.some((r) => r.ok)} onClick={() => void applyImport()}>
                Import {imported.result.results.filter((r) => r.ok).length} row{imported.result.results.filter((r) => r.ok).length === 1 ? "" : "s"}
              </Button>
            </>
          }
        >
          <div className="space-y-3 text-sm">
            <p className="text-muted">Each row is "email,actor". Rows with a problem are skipped; nothing is saved until you confirm.</p>
            <div className="max-h-80 overflow-y-auto rounded-lg border border-line">
              <table className="w-full">
                <tbody>
                  {imported.result.results.map((r) => (
                    <tr key={r.line} className="border-b border-line last:border-0">
                      <td className="px-3 py-1.5 text-muted tabular">{r.line}</td>
                      <td className="px-3 py-1.5">{r.email}</td>
                      <td className="px-3 py-1.5">{r.actor}</td>
                      <td className={cx("px-3 py-1.5", r.ok ? "text-good" : "text-bad")}>{r.ok ? "OK" : r.error}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}

function Section({ title, items, tone }: { title: string; items: string[]; tone?: "warn" | "muted" }) {
  if (!items.length) return null;
  return (
    <div>
      <div className={cx("font-medium mb-1", tone === "warn" ? "text-warn" : tone === "muted" ? "text-muted" : "text-ink")}>{title}</div>
      <ul className="max-h-40 overflow-y-auto rounded-lg bg-surface-2 px-3 py-2 space-y-0.5">
        {items.map((i) => (
          <li key={i}>{i}</li>
        ))}
      </ul>
    </div>
  );
}

function MappingRowView({ connId, row, onSaved }: { connId: string; row: Row; onSaved: () => void }) {
  const [actor, setActor] = useState(row.mapping?.actor ?? "");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const dirty = actor.trim() !== (row.mapping?.actor ?? "");
  const save = async () => {
    setBusy(true);
    setError(null);
    try {
      await api(`/admin/connections/${connId}/actors/${encodeURIComponent(row.email)}`, { method: "PUT", body: { actor: actor.trim() } });
      toast(actor.trim() ? `${row.email} mapped to ${actor.trim()}` : `Mapping removed for ${row.email}`);
      onSaved();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };
  return (
    <tr className={cx("border-b border-line last:border-0 align-top", row.status === "disabled" && "opacity-50")}>
      <td className="px-4 py-3">
        <div className="font-medium text-ink">{row.name || row.email}</div>
        {row.name && <div className="text-xs text-muted">{row.email}</div>}
        {row.status === "disabled" && <div className="text-xs text-muted">Disabled</div>}
      </td>
      <td className="px-4 py-3">
        <div className="flex gap-2">
          <input
            className="h-8 w-40 rounded-lg border border-line bg-surface px-2"
            value={actor}
            placeholder="Actor id"
            aria-label={`Infor actor for ${row.email}`}
            onChange={(e) => setActor(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && dirty && void save()}
          />
          {dirty && (
            <Button variant="primary" className="h-8" busy={busy} onClick={() => void save()}>
              {actor.trim() ? "Check and save" : "Remove"}
            </Button>
          )}
        </div>
        {row.mapping?.actorName && !dirty && <div className="text-xs text-muted mt-1">{row.mapping.actorName}</div>}
        {error && <div className="text-xs text-bad mt-1">{error}</div>}
      </td>
      <td className="px-4 py-3 text-xs text-muted">{row.mapping ? `${SOURCE[row.mapping.source]} · ${new Date(row.mapping.verifiedAt).toLocaleDateString()}` : <span className="text-warn">Not mapped</span>}</td>
    </tr>
  );
}
